import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import cx from 'classnames';
import { useHistory, Link } from 'react-router-dom';
import { get, find } from 'lodash';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import CtaLink from 'components/essentials/Cta/CtaLink';
import IconCloseEllipse from 'components/icons/iconCloseEllipse';
import {
  GET_CART,
  GET_RECENTLY_VIEWED,
  GET_CHECKOUT_SUMMARY,
  GET_FABRIC_DATA,
} from 'mocks/queries';
import {
  REMOVE_FROM_CART,
  UPDATE_CART_ITEM,
  APPLY_CUSTOMER_GIFTS_TO_CART,
} from 'mocks/mutations';
import { setLuxPageLabel, LUX_PAGE_LABELS } from 'commons/lux';
import CtaButton from 'components/essentials/Cta/CtaButton';
import refreshFinancingContent from 'commons/refreshFinancingContent';
import TabPanel from 'components/consumer/TabPanel';
import IconLock2 from 'components/icons/iconLock2';
import { sendFBEvent, FB_EVENTS } from 'commons/fbEvents';
import { logCriticalError } from 'commons/logger';
import learnMoreClick from 'commons/learnMoreClick';
import parseCartItemErrors from 'commons/parseCartItemErrors';

import getStoreDetails from 'commons/getStoreDetails';
import useUserData from 'data-hooks/useUserData';
import useScreen from 'hooks/useScreen';
import avo from 'analytics/avo';
import ProtectionPlanModal from 'components/consumer/ProtectionPlanModal';
import getZipcode from 'commons/getZipcode';
import useCloseCart from 'global-state/isCartOpen/useCloseCart';
import useResetProtectionModalCartItemId from 'global-state/protectionModal/useResetProtectionModalCartItemId';
import useSetProtectionModalCartItemId from 'global-state/protectionModal/useSetProtectionModalCartItemId';
import useProtectionModalCartItemId from 'global-state/protectionModal/useProtectionModalCartItemId';
import CartProduct from './product';
import ApplyCoupon from './ApplyCoupon';
import { getAnalyticsDataFromCartItem, getRecentlyViewed } from './utils';
import CheckoutWithApplePay from './components/CheckoutWithApplePay';
import CartAffirmSection from './CartAffirmSection';
import CartRecentlyViewedProducts from './CartRecentlyViewedProducts';

const emptyFunction = () => {};

const Cart = ({
  openShareModal,
  setLockScrollWhenOpen,
  stickyFooter,
  viewType,
}) => {
  const cartRef = useRef();
  const history = useHistory();
  const { isMobile } = useScreen();
  const { isTradeUser } = useUserData();
  const closeCart = useCloseCart();
  const protectionModalCartItemId = useProtectionModalCartItemId();

  const isCartOnlyView = viewType === 'cart-only';
  const closeCartCallback = !isCartOnlyView ? closeCart : emptyFunction;

  const [state, setState] = useState({
    loading: false,
    removeItemLoading: {},
    selectedTab: 'your-bag',
    applyCouponEditMode: false,
    isCartRecommendationVariant: false,
  });

  const affirmButtonRef = useRef();

  // mutations
  const [updateCartItemMutation] = useMutation(UPDATE_CART_ITEM);
  const [removeItemFromCartMutation] = useMutation(REMOVE_FROM_CART);
  const [applyCustomerGiftsToCartMutation] = useMutation(
    APPLY_CUSTOMER_GIFTS_TO_CART
  );

  // queries
  const zipCode = getZipcode();
  const { data: fabricData } = useSuspenseQuery(GET_FABRIC_DATA);
  const { data: recentlyViewedData } = useSuspenseQuery(GET_RECENTLY_VIEWED);
  const {
    data: cartData,
    error: cartError,
    refetch: refetchCart,
  } = useSuspenseQuery(GET_CART, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
    variables: { zipCode },
  });
  const {
    data: checkoutSummaryData,
    refetch: refetchCheckoutSummary,
  } = useSuspenseQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        zipcode: zipCode,
      },
    },
  });

  // memos
  const fabricsData = useMemo(() => fabricData?.getFabrics, [fabricData]);
  const { checkoutSummary, orderTotal } = useMemo(() => {
    const summary = checkoutSummaryData?.checkoutSummary;
    const {
      cart_subtotal,
      base_discount_amount,
      stock_discount,
      protectionSubtotal,
    } = summary || {};
    return {
      checkoutSummary: summary,
      orderTotal:
        (cart_subtotal || 0) +
        (protectionSubtotal || 0) -
        (base_discount_amount > 0 ? base_discount_amount : 0) -
        (stock_discount > 0 ? stock_discount : 0),
    };
  }, [checkoutSummaryData]);
  const { products, cartToken, orderDiscount, cartIsEmpty } = useMemo(() => {
    const items = get(cartData, 'getCart.cart') ?? [];
    return {
      products: items,
      cartIsEmpty: !items?.length,
      cartToken: get(cartData, 'getCart.cart_token', ''),
      orderDiscount:
        items?.reduce((acc, val) => acc + val.discount_amount, 0) ?? 0,
    };
  }, [cartData]);

  const { eligiblePlans, selectedPlan } = useMemo(() => {
    const cartItem = products.filter(
      item => item.id === protectionModalCartItemId
    )[0];
    return {
      eligiblePlans: cartItem ? cartItem.eligible_protection_plans : [],
      selectedPlan: cartItem ? cartItem.selected_protection_plan : null,
    };
  }, [products, protectionModalCartItemId]);

  // callbacks
  const getRecentlyViewedIds = useCallback(
    () =>
      recentlyViewedData?.recentlyViewedIds?.length
        ? recentlyViewedData.recentlyViewedIds
        : getRecentlyViewed(),
    [recentlyViewedData]
  );

  const onSelectTab = useCallback(
    id => setState(prevState => ({ ...prevState, selectedTab: id })),
    []
  );

  const onViewCart = useCallback(() => {
    const fabricsObj =
      fabricsData?.reduce((acc, cur) => {
        acc[cur.option_value_id] = cur;
        return acc;
      }, {}) ?? {};

    avo.cartViewed({
      cartId: cartToken,
      products:
        products.map(item => {
          let variant = '';
          item.options.forEach((option, index) => {
            if (index === 0) {
              variant += `${option.name}`;
            } else {
              variant += `, ${option.name}`;
            }
          });
          const cartProduct = getAnalyticsDataFromCartItem(item);
          const fabricOption = find(item.options, { optionTypeId: 17 });
          if (!cartProduct.pickLater && fabricsObj?.[fabricOption?.id]) {
            cartProduct.fabricColor = fabricsObj[fabricOption.id].option_color;
            cartProduct.fabricName = fabricsObj[fabricOption.id].option_value;
          }
          return {
            productId: parseInt(item.product_id, 10),
            category: item.product.type.name,
            isClearance: item.is_clearance ? item.is_clearance : false,
            name: item.name,
            price: parseFloat(item.price),
            fullPrice: parseFloat(item.price_pre_discount),
            quantity: +item.quantity,
            sku: item.product.sku + item.sku,
            variant,
            isFlashSale: !!item?.flash_sale,
            fabricName: cartProduct.fabricName,
            fabricColor: cartProduct.fabricColor,
            currency: 'USD',
            shippingMethod: 'Curbside',
            shippingCarrier: 'Custom',
          };
        }) || [],
    });
  }, [cartToken, fabricsData, products]);

  const resetProtectionModalCartItemId = useResetProtectionModalCartItemId();
  const setProtectionModalCartItemId = useSetProtectionModalCartItemId();

  const applyCustomerGiftsToCart = useCallback(
    async apply => {
      setState(prevState => ({ ...prevState, loading: true }));
      await applyCustomerGiftsToCartMutation({
        variables: { apply },
      });
      await refetchCheckoutSummary({
        input: {
          zipcode: getZipcode(),
        },
      });
      setState(prevState => ({ ...prevState, loading: false }));
    },
    [applyCustomerGiftsToCartMutation, refetchCheckoutSummary]
  );

  const removeItem = useCallback(
    async item => {
      setState(prevState => ({
        ...prevState,
        removeItemLoading: { ...prevState.removeItemLoading, [item.id]: true },
      }));
      await removeItemFromCartMutation({
        variables: {
          id: item.id,
        },
      });
      await refetchCart({ zipCode: getZipcode() });
      setState(prevState => ({
        ...prevState,
        removeItemLoading: { ...prevState.removeItemLoading, [item.id]: false },
      }));
    },
    [refetchCart, removeItemFromCartMutation]
  );

  const removeExtraCushion = useCallback(
    async item => {
      const zipcode = getZipcode();
      setState(prevState => ({ ...prevState, loading: true }));
      await updateCartItemMutation({
        variables: {
          cart_id: item.id,
          quantity: item.quantity,
          product_id: item.product_id.toString(),
          pickLater: item.pickLater,
          productOptions: item.options.map(option => {
            const optionTypeId = parseInt(option.optionTypeId, 10);
            return {
              option_id: optionTypeId,
              option_value_id: parseInt(
                optionTypeId === 133 ? option.default_value : option.value,
                10
              ),
            };
          }),
          zipCode: zipcode,
        },
      });
      await refetchCart({ zipCode: zipcode });
      setState(prevState => ({ ...prevState, loading: false }));
    },
    [refetchCart, updateCartItemMutation]
  );

  const onCheckoutStarted = useCallback(async () => {
    const fabricsObj =
      fabricsData?.reduce((acc, cur) => {
        acc[cur.option_value_id] = cur;
        return acc;
      }, {}) ?? {};
    const storeDetails = getStoreDetails();

    avo.checkoutStarted({
      products:
        products.map(item => {
          let variant = '';
          item.options.forEach((option, index) => {
            if (index === 0) {
              variant += `${option.name}`;
            } else {
              variant += `, ${option.name}`;
            }
          });
          const cartProduct = getAnalyticsDataFromCartItem(item);
          const fabricOption = find(item.options, { optionTypeId: 17 });
          if (
            !cartProduct.pickLater &&
            fabricOption &&
            fabricsObj?.[fabricOption?.id]
          ) {
            cartProduct.fabricColor =
              fabricsObj?.[fabricOption.id].option_color;
            cartProduct.fabricName = fabricsObj?.[fabricOption.id].option_value;
          }
          return {
            productId: parseInt(item.product_id, 10),
            category: item.product.type.name,
            isClearance: item.is_clearance ? item.is_clearance : false,
            name: item.name,
            price: parseFloat(item.price),
            fullPrice: parseFloat(item.price_pre_discount),
            quantity: +item.quantity,
            sku: item.product.sku + item.sku,
            variant,
            currency: 'USD',
            shippingMethod: 'Custom',
            shippingCarrier: 'Curbside',
            fabricName: cartProduct.fabricName,
            fabricColor: cartProduct.fabricColor,
            isFlashSale: !!item?.flash_sale,
          };
        }) || [],
      affiliation: storeDetails ? storeDetails.name : 'Online', // todo
      tax: 0,
      shipping: 0,
      currency: 'USD',
      discount: parseFloat(orderDiscount),
      revenue: parseFloat(orderTotal),
      cartId: cartToken,
    });
  }, [cartToken, fabricsData, orderDiscount, orderTotal, products]);

  const sendCheckoutEvent = useCallback(() => {
    const skus = [];
    let totalPrice = 0;
    products.map(getAnalyticsDataFromCartItem).forEach(prod => {
      skus.push(prod.sku);
      totalPrice += prod.discountPrice;
    });

    sendFBEvent(FB_EVENTS.CHECKOUT, {
      content_ids: skus,
      content_type: 'product',
      currency: 'USD',
      value: totalPrice,
    });
    setLuxPageLabel(LUX_PAGE_LABELS.CHECKOUT);
    onCheckoutStarted();
  }, [onCheckoutStarted, products]);

  const getCreditSection = useCallback(
    (variant = '') =>
      checkoutSummary?.gift_card_rewards_total > 0 &&
      (checkoutSummary?.gift_card_rewards_used > 0 ? (
        <div
          className={cx('flex justify-between flex-row flex-wrap mb-2', {
            'mb-2': variant === 'mobile',
          })}
        >
          <div className="credits text-red">
            Credits
            <span className="relative cursor-pointer top-[3px] left-2">
              <IconCloseEllipse
                onClick={() => applyCustomerGiftsToCart(false)}
              />
            </span>
          </div>
          <div className="text-red" data-xc="cart-credits-value">
            - ${checkoutSummary.gift_card_rewards_used}
          </div>
        </div>
      ) : (
        <CtaButton
          hasArrow={false}
          onClick={() => applyCustomerGiftsToCart(true)}
          styling="link"
          color="brand"
          size="xs"
          spacing="sm"
        >
          Apply available credit: ${checkoutSummary?.gift_card_rewards_total}
        </CtaButton>
      )),
    [
      applyCustomerGiftsToCart,
      checkoutSummary?.gift_card_rewards_total,
      checkoutSummary?.gift_card_rewards_used,
    ]
  );

  const getSaveForLaterButton = useCallback(
    () => (
      <div className="mt-[10px] p-0 md:p-[unset]" data-xc="share-button">
        <CtaButton
          block
          onClick={openShareModal}
          styling="outline-button"
          color="black"
          spacing="sm"
        >
          Share your cart
        </CtaButton>
      </div>
    ),
    [openShareModal]
  );

  const getProductsInCart = useCallback(
    () =>
      products.map((prod, idx) => (
        <CartProduct
          product={prod}
          // eslint-disable-next-line react/no-array-index-key
          key={`${prod.id}-${idx}`}
          hasImageLink
          closeCart={closeCartCallback}
          removeItem={removeItem}
          removeItemLoading={state.removeItemLoading[prod.id]}
          removeExtraCushion={removeExtraCushion}
          openProtectionModal={cartItemId => {
            setProtectionModalCartItemId(cartItemId);
          }}
          cartToken={cartToken}
          viewType={viewType}
        />
      )),
    [
      cartToken,
      closeCartCallback,
      products,
      removeExtraCushion,
      removeItem,
      setProtectionModalCartItemId,
      state.removeItemLoading,
      viewType,
    ]
  );

  const handleFinancingClick = useCallback(() => {
    setLockScrollWhenOpen(false);
    learnMoreClick(affirmButtonRef);
  }, [setLockScrollWhenOpen, affirmButtonRef]);

  const getCheckoutAndQuoteButtonsWithAffirm = useCallback(() => {
    const cartItemsWithIssues = products.filter(prod => {
      const { issue } = parseCartItemErrors(prod && prod.cart_item_errors);
      return !!issue;
    });
    return (
      <>
        <div className="bg-white first:mt-0 py-[10px] md:py-[unset] px-0 md:px-[unset] sticky top-0 z-[11]">
          <div className="mt-[10px]" data-xc="checkout-button">
            <CtaButton
              disabled={state.loading || cartItemsWithIssues.length}
              onClick={() => {
                sendCheckoutEvent();
                closeCartCallback();
                history.push('/checkout/');
              }}
              styling="solid-button"
              color="black"
              block
              spacing="sm"
              isLoading={state.loading}
            >
              <IconLock2 /> Proceed to Checkout
            </CtaButton>
          </div>
          <CheckoutWithApplePay
            orderTotal={orderTotal}
            checkoutSummary={checkoutSummary}
            products={products}
          />
          {isTradeUser && (
            <div className="mt-[10px]" data-xc="quote-button">
              <CtaButton
                onClick={() => {
                  closeCartCallback();
                  history.push('/quote/new/');
                }}
                styling="solid-button"
                color="brand"
                block
                spacing="sm"
              >
                Create A Quote
              </CtaButton>
            </div>
          )}
        </div>
        {!isCartOnlyView && getSaveForLaterButton()}

        {!isTradeUser && !stickyFooter && (
          <CartAffirmSection
            orderTotal={orderTotal}
            handleFinancingClick={handleFinancingClick}
            affirmButtonRef={affirmButtonRef}
          />
        )}
      </>
    );
  }, [
    checkoutSummary,
    closeCartCallback,
    getSaveForLaterButton,
    handleFinancingClick,
    history,
    isCartOnlyView,
    isTradeUser,
    orderTotal,
    products,
    sendCheckoutEvent,
    state.loading,
    stickyFooter,
  ]);

  const getMobileTotalSection = useCallback(
    () => (
      <div className="pt-5 px-0 pb-[10px] text-lg text-gray font-bold md:hidden">
        <ApplyCoupon variant="mobileTotalSection" />
        {getCreditSection('mobile')}
        <div className="flex flex-row flex-wrap justify-between">
          <span className="subtotal-title">Subtotal </span>
          <span className="subtotal-text" data-xc="cart-subtotal-value">
            {orderTotal !== '' ? '$' : ''}
            {orderTotal.toLocaleString()}
          </span>
        </div>
      </div>
    ),
    [orderTotal, getCreditSection]
  );

  const getDesktopTotalSection = useCallback(
    () => (
      <>
        <ApplyCoupon
          onEdit={mode =>
            setState(prevState => ({
              ...prevState,
              applyCouponEditMode: !!mode,
            }))
          }
        />
        {getCreditSection()}
        {!cartIsEmpty && (
          <div className="mb-4 flex justify-between py-2 px-0 flex-row flex-wrap">
            <div className="text-sm font-bold">Subtotal</div>
            <div
              className="text-sm text-gray font-bold"
              data-xc="cart-subtotal-value"
            >
              {orderTotal !== '' ? '$' : ''}
              {orderTotal.toLocaleString()}
            </div>
          </div>
        )}
      </>
    ),
    [setState, orderTotal, cartIsEmpty, getCreditSection]
  );

  const getDesktopFooter = useCallback(
    () => (
      <div
        className={cx({
          'bg-white py-0 px-8': stickyFooter,
          'pb-6': !stickyFooter,
        })}
      >
        {!isMobile && (
          <>
            {getDesktopTotalSection()}
            {getCheckoutAndQuoteButtonsWithAffirm()}
          </>
        )}
      </div>
    ),
    [
      stickyFooter,
      isMobile,
      getDesktopTotalSection,
      getCheckoutAndQuoteButtonsWithAffirm,
    ]
  );

  const getCartContents = useCallback(() => {
    if (cartIsEmpty) {
      return (
        <div>
          <div
            className={cx('text-xl font-bold pt-0 pb-5 px-0', {
              'm:mt-4 md:mt-[unset]': isCartOnlyView,
            })}
          >
            Your bag is empty
          </div>

          <div className="text-sm text-gray mb-6">
            Need some inspiration? Explore{' '}
            <Link
              className="text-brand hover:underline"
              to="/customer-photos/"
              onClick={closeCartCallback}
            >
              customer photos
            </Link>{' '}
            or our wide range of{' '}
            <Link
              className="text-brand hover:underline"
              to="/swatches/"
              onClick={closeCartCallback}
            >
              textures and materials
            </Link>
            .
          </div>

          <div data-xc="shop-now-button">
            <CtaLink
              to="/shop/"
              styling="solid-button"
              size="sm"
              color="brand"
              onClick={closeCartCallback}
              block={isMobile}
              spacing="sm"
            >
              <span>Start shopping</span>
            </CtaLink>
          </div>
        </div>
      );
    }

    return (
      <>
        {isMobile && (
          <>
            {getMobileTotalSection()}
            {getCheckoutAndQuoteButtonsWithAffirm()}
          </>
        )}

        {getProductsInCart()}
        {!stickyFooter && getDesktopFooter()}
      </>
    );
  }, [
    cartIsEmpty,
    closeCartCallback,
    getCheckoutAndQuoteButtonsWithAffirm,
    getDesktopFooter,
    getMobileTotalSection,
    getProductsInCart,
    isCartOnlyView,
    isMobile,
    stickyFooter,
  ]);

  const renderCart = useCallback(
    () => (
      <>
        {orderDiscount !== 0 && (
          <div
            className={cx(
              'text-xs py-[1.1rem] px-12 bg-brand-lightest text-brand-dark font-normal relative flex justify-center items-center text-center',
              {
                '-mx-4': isCartOnlyView,
              }
            )}
          >
            <span>
              {`Nice! You've `}
              <span className="font-[700]">
                saved ${orderDiscount.toLocaleString()}
              </span>{' '}
              on your order!
            </span>
          </div>
        )}

        {getCartContents()}

        {!isTradeUser && stickyFooter && (
          <CartAffirmSection
            orderTotal={orderTotal}
            handleFinancingClick={handleFinancingClick}
            affirmButtonRef={affirmButtonRef}
          />
        )}
      </>
    ),
    [
      getCartContents,
      handleFinancingClick,
      isCartOnlyView,
      isTradeUser,
      orderDiscount,
      orderTotal,
      stickyFooter,
    ]
  );

  const getRecentlyViewedPanelContent = useCallback(() => {
    const recentlyViewedIds = getRecentlyViewedIds();

    return (
      <CartRecentlyViewedProducts
        productIds={recentlyViewedIds.map(pId => parseInt(pId, 10))}
        onClick={closeCartCallback}
      />
    );
  }, [getRecentlyViewedIds, closeCartCallback]);

  const getTabletAndDesktopTabs = useCallback(() => {
    const recentlyViewedIds = getRecentlyViewedIds();
    if (recentlyViewedIds.length === 0) {
      return [
        {
          label: 'Your Bag',
          id: 'your-bag',
          contentId: 'your-bag-content',
          content: renderCart(),
        },
      ];
    }

    return [
      {
        label: 'Your Bag',
        id: 'your-bag',
        contentId: 'your-bag-content',
        content: renderCart(),
      },
      {
        label: 'Recently Viewed ',
        id: 'recently-viewed',
        contentId: 'recently-viewed-content',
        content: getRecentlyViewedPanelContent(),
        badgeValue: recentlyViewedIds.length,
      },
    ];
  }, [getRecentlyViewedPanelContent, getRecentlyViewedIds, renderCart]);

  const getMobileAndEmptyCartContent = useCallback(
    isDesktop => (
      <div
        className={cx({
          'px-8 pt-16 pb-0': !!isDesktop,
        })}
      >
        {renderCart()}
      </div>
    ),
    [renderCart]
  );

  useEffect(() => {
    onViewCart();
    refreshFinancingContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cartError) {
      logCriticalError(cartError, 'GET_CART_FAILURE');
    }
  }, [cartError]);

  useEffect(() => {
    refreshFinancingContent();
  }, [orderTotal]);

  const hasStickyFooter = !!stickyFooter && state.selectedTab === 'your-bag';

  return (
    <div
      className={cx(
        'Cart',
        {
          'py-0 px-4 md:p-[unset]': isCartOnlyView,
        },
        {
          'bg-white': !!stickyFooter,
        }
      )}
    >
      <ProtectionPlanModal
        cartItemId={protectionModalCartItemId}
        close={() => {
          resetProtectionModalCartItemId();
        }}
        eligiblePlans={eligiblePlans}
        isOpen={!!protectionModalCartItemId}
        selectedPlan={selectedPlan}
      />

      <div
        ref={cartRef}
        id="cart-component"
        className={cx('p-0', {
          'w-full md:w-[unset]': isCartOnlyView,
        })}
        tabIndex="-1"
      >
        <div
          className={cx(
            'pb-0 md:pb-[unset] overflow-x-[initial] overflow-y-[initial]',
            {
              hidden: !!protectionModalCartItemId,
            }
          )}
        >
          {isMobile ? (
            getMobileAndEmptyCartContent(false)
          ) : cartIsEmpty ? (
            getMobileAndEmptyCartContent(true)
          ) : isCartOnlyView ? (
            <div className="py-0 md:py-[unset] px-4 md:px-[unset]">
              <div className="md:block md:text-xl md:font-bold md:text-center md:text-gray hidden">
                Your bag
              </div>

              <div className="mt-5">{renderCart()}</div>
            </div>
          ) : (
            <div
              className={cx({
                'h-[100vh-22rem]': hasStickyFooter && state.applyCouponEditMode,
              })}
            >
              <TabPanel
                aria-label="Cart"
                items={getTabletAndDesktopTabs()}
                onSelectTab={onSelectTab}
                selectedTab={state.selectedTab}
                classNameExtension={{
                  '!bg-gray-light9': !!stickyFooter,
                  '!px-8 !pt-16 !pb-0': true,
                  '!overflow-y-auto !h-[100vh-20rem] !border-b !border-solid !border-gray-light7': hasStickyFooter,
                }}
                tabClassNamesOverride="!text-base !font-bold"
                tabGroupsClassNamesOverride={
                  isCartOnlyView ? '!justify-between' : ''
                }
                selectedTabClassNamesOverride={
                  isCartOnlyView ? '!flex-[1] !text-2xl' : ''
                }
                tabContentClassNamesOverride={
                  isCartOnlyView ? '!py-0 !px-5' : ''
                }
              />
              {hasStickyFooter && getDesktopFooter()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Cart.propTypes = {
  cart: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        product_id: PropTypes.string,
        quantity: PropTypes.number,
      })
    ),
  }),
  checkoutSummary: PropTypes.shape({
    base_discount_amount: PropTypes.number,
    cart_subtotal: PropTypes.number,
    gift_card_rewards_total: PropTypes.number,
    gift_card_rewards_used: PropTypes.number,
    protectionSubtotal: PropTypes.number,
  }),
  data: PropTypes.shape({
    cart: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          product_id: PropTypes.string,
          quantity: PropTypes.number,
        })
      ),
    }),
  }),
  stickyFooter: PropTypes.bool,
  openShareModal: PropTypes.func,
  setLockScrollWhenOpen: PropTypes.func,
  viewType: PropTypes.string,
};

export default Cart;
